import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Tooltip,
    Chip,
    Alert,
    CircularProgress,
    alpha,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { T } from '../theme/tokens';
import { SynButton } from './ds';
import { getAdminCode, setAdminCode, clearAdminCode, hasAdminCode } from '../config';
import { apiFetch } from '../api/client';

/** User-facing messages for the two backend rejection modes. */
const MSG_REJECTED =
    'That code does not match the current SYNTERRA_ADMIN_CODE. This modal expects the ' +
    'internal admin code (the SYNTERRA_ADMIN_CODE secret held by the platform owner) — ' +
    'not your staff sign-in invitation code or the research-preview code. If you should ' +
    'have admin access, ask the platform owner for the current code.';
const MSG_UNCONFIGURED =
    'The SYNTERRA_ADMIN_CODE secret is not configured on this deployment, so admin ' +
    'access cannot be unlocked. Ask the platform owner to set it.';
const MSG_OFFLINE = 'Could not reach the server to verify the admin code. Try again.';

/**
 * Validate an admin code against backend truth (POST /auth/verify-admin —
 * same SYNTERRA_ADMIN_CODE semantics as require_admin). Returns null when the
 * code is valid, otherwise a user-facing error message. Network/transient
 * errors return MSG_OFFLINE and must NOT store or clear anything.
 */
async function verifyAdminCode(code: string): Promise<string | null> {
    const resp = await apiFetch('/auth/verify-admin', {
        method: 'POST',
        json: { code },
        context: 'public',
    });
    if (resp.ok) return null;
    if (resp.status === 401) return MSG_REJECTED;
    if (resp.status === 503) return MSG_UNCONFIGURED;
    // Other server errors: treat as transient, don't relabel the code invalid.
    throw new Error(`verify-admin ${resp.status}`);
}

/**
 * Staff-only control for the admin/internal API credential.
 *
 * SECURITY: this is the SYNTERRA_ADMIN_CODE, intentionally SEPARATE from the
 * public research-preview code held by AccessGate. Entering it here VALIDATES
 * the code against the live backend first and only then stores it under
 * `spartis_admin_code` (see config.ts) so `adminFetch` can send it as
 * `X-Admin-Code`. A rejected code is never stored — the chip must reflect
 * backend truth, not local state. The preview gate never grants admin access.
 */
export default function AdminAccessControl() {
    const [open, setOpen] = useState(false);
    const [unlocked, setUnlocked] = useState<boolean>(hasAdminCode);
    const [code, setCode] = useState('');
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState('');

    // Keep the lock indicator in sync if the admin code changes elsewhere
    // (e.g. another tab, or a 401 handler clearing it).
    useEffect(() => {
        const sync = () => setUnlocked(hasAdminCode());
        window.addEventListener('spartis:adminCodeChanged', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('spartis:adminCodeChanged', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    // A previously-stored (possibly stale) code must not keep showing "Admin"
    // if the backend now rejects it — silently re-verify once on mount and
    // relock if it is definitively rejected (401/503). Transient errors keep
    // the stored code (fail open; every admin API still enforces per-request).
    useEffect(() => {
        const stored = getAdminCode();
        if (!stored) return;
        let cancelled = false;
        verifyAdminCode(stored)
            .then(msg => {
                if (cancelled || msg === null) return;
                clearAdminCode();
                setUnlocked(false);
                setError(msg);
            })
            .catch(() => { /* transient — keep the stored code */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpen = () => {
        setCode(getAdminCode());
        // Deliberately KEEP any pending error (e.g. the mount-time stale-code
        // rejection) so the user sees WHY they were relocked when they open
        // the dialog; typing clears it.
        setOpen(true);
    };

    const handleSave = async () => {
        const entered = code.trim();
        if (!entered) return;
        setChecking(true);
        setError('');
        try {
            const msg = await verifyAdminCode(entered);
            if (msg !== null) {
                // Backend rejected: never store, relock if a stale code existed.
                clearAdminCode();
                setUnlocked(false);
                setError(msg);
                return;
            }
            setAdminCode(entered);
            setUnlocked(true);
            setOpen(false);
        } catch {
            setError(MSG_OFFLINE);
        } finally {
            setChecking(false);
        }
    };

    const handleClear = () => {
        clearAdminCode();
        setCode('');
        setError('');
        setUnlocked(false);
        setOpen(false);
    };

    return (
        <>
            <Tooltip title={unlocked ? 'Admin access unlocked — manage code' : (error || 'Enter admin access code')}>
                <Chip
                    icon={unlocked
                        ? <LockOpenIcon sx={{ fontSize: 14 }} />
                        : <LockIcon sx={{ fontSize: 14 }} />}
                    label={unlocked ? 'Admin' : 'Admin locked'}
                    size="small"
                    onClick={handleOpen}
                    aria-label="admin access"
                    sx={{
                        height: 22,
                        fontSize: '0.63rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: unlocked
                            ? alpha(T.color.teal[700], 0.07)
                            : alpha(T.color.coral[500], 0.10),
                        color: unlocked ? T.color.teal[600] : T.color.coral[500],
                        border: `1px solid ${unlocked
                            ? alpha(T.color.teal[700], 0.12)
                            : alpha(T.color.coral[500], 0.22)}`,
                        '& .MuiChip-icon': { color: 'inherit' },
                    }}
                />
            </Tooltip>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Admin access</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.85rem', color: T.color.text.muted, mb: 2, lineHeight: 1.6 }}>
                        Enter the internal admin code (the <strong>SYNTERRA_ADMIN_CODE</strong> secret,
                        held by the platform owner) to use staff dashboards and internal APIs.
                        This is a separate system from staff sign-in (email + invitation code)
                        and from the research-preview access code — none of those codes work here.
                        Don&apos;t have it? Ask the platform owner, or use Staff sign-in instead.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        type="password"
                        label="Admin code"
                        placeholder="Admin code"
                        value={code}
                        autoComplete="off"
                        onChange={e => { setCode(e.target.value); setError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter' && code.trim()) handleSave(); }}
                    />
                    {error && (
                        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    {unlocked && (
                        <SynButton variant="ghost" onClick={handleClear} sx={{ mr: 'auto' }}>
                            Lock / clear
                        </SynButton>
                    )}
                    <SynButton variant="ghost" onClick={() => setOpen(false)}>
                        Cancel
                    </SynButton>
                    <SynButton variant="primary" onClick={handleSave} disabled={checking || !code.trim()}>
                        {checking ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Verify & unlock'}
                    </SynButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
